import { z } from "zod";
import { folderIdSchema, optionalFolderIdSchema } from "./folders.ts";
import {
  classificationLevelSchema,
  documentIdSchema,
  storedDocumentOriginSchema,
  storedTrustSchema,
} from "./knowledge.ts";
import { projectIdSchema } from "./projects.ts";
import { userIdSchema } from "./tenancy.ts";

/**
 * Conversation artefacts (openspec change `artefacts-text`): standalone text content the agent
 * creates and revises in a conversation, one immutable version per revision. Only the agent
 * writes them (`create_artefact`, `update_artefact`); clients list, read and download versions.
 */

export const artefactIdSchema = z.uuid().brand<"ArtefactId">();
export type ArtefactId = z.infer<typeof artefactIdSchema>;

/** The kinds the agent writes as text (`create_artefact`). */
export const TEXT_ARTEFACT_KINDS = ["markdown", "code", "mermaid", "svg", "csv"] as const;
export const textArtefactKindSchema = z.enum(TEXT_ARTEFACT_KINDS);
export type TextArtefactKind = z.infer<typeof textArtefactKindSchema>;

/**
 * `code` carries a language; `mermaid` and `svg` are diagram sources; `csv` is a table; `file`
 * is a binary a code run produced (agent-turn D19): bytes in the object store, never text.
 */
export const artefactKindSchema = z.enum([...TEXT_ARTEFACT_KINDS, "file"]);
export type ArtefactKind = z.infer<typeof artefactKindSchema>;

/**
 * Limits of file artefacts (agent-turn D18, D19): a file version is at most 20 MB of an allowed
 * type; a code run keeps at most 10 files and 5 printed images (2 MB each).
 */
export const FILE_ARTEFACT_LIMITS = {
  bytes: 20 * 1024 * 1024,
  filesPerRun: 10,
  imagesPerRun: 5,
  imageBytes: 2 * 1024 * 1024,
  nameChars: 120,
} as const;

/**
 * The types a file artefact may have, by extension (D19): anything else is dropped and the model
 * told. SVG is served as a file (an attachment), never drawn inline from a code run.
 */
export const FILE_ARTEFACT_TYPES: Readonly<Record<string, string>> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  svg: "image/svg+xml",
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
  json: "application/json",
  txt: "text/plain",
  md: "text/markdown",
  zip: "application/zip",
};

/** The types an image preview is served inline as (through the API, never a presigned URL). */
export const FILE_ARTEFACT_PREVIEW_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
]);

/** The allowed media type of a file name, by its extension; null when it is not allowed. */
export function fileArtefactType(name: string): string | null {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) {
    return null;
  }
  return FILE_ARTEFACT_TYPES[name.slice(dot + 1).toLowerCase()] ?? null;
}

/**
 * Limits of artefacts (design §1; the four product limits approved on 2026-10-24). The tools'
 * input schemas carry them, and core enforces them again before anything is stored.
 */
export const ARTEFACT_LIMITS = {
  /** Characters (UTF-16 code units, as the model's input is measured) of one version. */
  contentChars: 100_000,
  /** UTF-8 bytes of one version. */
  contentBytes: 256 * 1024,
  titleChars: 120,
  versionsPerArtefact: 30,
  artefactsPerConversation: 20,
  /** `create_artefact` and `update_artefact` calls stored by one turn. */
  writesPerTurn: 8,
  editsPerCall: 20,
  csvRows: 10_000,
  csvColumns: 200,
  languageChars: 32,
} as const;

/** A code artefact's language: a free lower-case token such as `python`, `sql` or `c++`. */
export const artefactLanguageSchema = z
  .string()
  .min(1)
  .max(ARTEFACT_LIMITS.languageChars)
  .regex(/^[a-z0-9+#-]+$/, "A lower-case token such as python, sql or c++");

export const artefactTitleSchema = z.string().trim().min(1).max(ARTEFACT_LIMITS.titleChars);

/** The per-conversation handle the agent names an artefact by (`A1`, `A2`, …). */
export const artefactHandleSchema = z.string().regex(/^A[1-9][0-9]*$/);

/** One artefact as listed for a viewer: its newest version the viewer passes. */
export const artefactSummarySchema = z.object({
  id: artefactIdSchema,
  handle: artefactHandleSchema,
  title: z.string(),
  kind: artefactKindSchema,
  language: z.string().nullable(),
  /** The newest version the viewer passes. */
  version: z.int().min(1),
  /** How many of its versions the viewer passes. */
  versions: z.int().min(1),
  /** When that version was written. */
  updatedAt: z.iso.datetime(),
  /** A file artefact's version: its media type and size (optional for one release). */
  file: z.object({ contentType: z.string(), sizeBytes: z.int().min(0) }).optional(),
});
export type ArtefactSummary = z.infer<typeof artefactSummarySchema>;

export const artefactListSchema = z.object({ artefacts: z.array(artefactSummarySchema) });

/**
 * `GET …/artefacts?files=1`: include file artefacts (agent-turn D19). Clients that render them
 * ask; the previous release's app, which does not know the kind, never receives one.
 */
export const artefactListQuerySchema = z.object({ files: z.literal("1").optional() });
export type ArtefactList = z.infer<typeof artefactListSchema>;

/** One version with its content: untrusted model output, never to be rendered as HTML. */
export const artefactVersionSchema = z.object({
  id: artefactIdSchema,
  handle: artefactHandleSchema,
  title: z.string(),
  kind: artefactKindSchema,
  language: z.string().nullable(),
  version: z.int().min(1),
  /** Empty for a file artefact: its bytes are downloaded, never inlined. */
  content: z.string(),
  createdAt: z.iso.datetime(),
  trust: storedTrustSchema,
  /** A file artefact's media type and size (optional for one release). */
  file: z.object({ contentType: z.string(), sizeBytes: z.int().min(0) }).optional(),
});
export type ArtefactVersion = z.infer<typeof artefactVersionSchema>;

// ── Adding a version to knowledge (openspec change `artefacts-to-knowledge`) ─────────────

/**
 * `POST …/artefacts/:artefactId/versions/:version/knowledge`: add the version to a project's
 * knowledge (sink `artefact_add`). The readers and classification come from the version's label;
 * no input widens them, and there is no declassification.
 */
export const addArtefactToKnowledgeRequestSchema = z.object({
  /** The file name; defaults to the version's title with its kind's extension. */
  title: z.string().trim().min(1).max(255).optional(),
  /** Required from a standalone conversation (a project the caller may upload to), refused otherwise. */
  targetProjectId: projectIdSchema.optional(),
  /** A Files folder of the target project; the Files root when absent or null. */
  folderId: optionalFolderIdSchema,
});
export type AddArtefactToKnowledgeRequest = z.infer<typeof addArtefactToKnowledgeRequestSchema>;

/** `GET …/knowledge?targetProjectId=&folderId=`: the preview's target. */
export const artefactKnowledgeQuerySchema = z.object({
  targetProjectId: z.uuid().optional(),
  folderId: z.uuid().optional(),
});

/** `POST …/knowledge/update`: make this (newer) version the added document's next version. */
export const updateArtefactInKnowledgeRequestSchema = z.object({
  targetProjectId: projectIdSchema.optional(),
});
export type UpdateArtefactInKnowledgeRequest = z.infer<
  typeof updateArtefactInKnowledgeRequestSchema
>;

/**
 * The artefact's document in the target project, when one is live: the artefact version it was
 * last updated from, and whether the caller may update it (its uploader, a manager or an owner).
 */
export const artefactInKnowledgeSchema = z.object({
  documentId: documentIdSchema,
  documentVersion: z.int().min(1),
  artefactVersion: z.int().min(1),
  folderId: folderIdSchema.nullable(),
  canUpdate: z.boolean(),
});
export type ArtefactInKnowledge = z.infer<typeof artefactInKnowledgeSchema>;

/**
 * What adding (or updating) would create, computed for the caller without writing: who could
 * read the file right now, by name, its classification (the most sensitive of the project
 * default and its sources' current levels), its origin (`generated`, or `external`), and the
 * target folder's path. `state`: `add`, `added` (this version is already in knowledge), or
 * `update` (an earlier version is: the action is "Update in knowledge").
 */
export const artefactKnowledgePreviewSchema = z.object({
  state: z.enum(["add", "added", "update"]),
  projectId: projectIdSchema,
  title: z.string(),
  /** By name for named readers only; empty for the project audience (see `readerCount`). */
  readers: z.array(z.object({ userId: userIdSchema, displayName: z.string().nullable() })),
  /** How many people could read the file right now. */
  readerCount: z.int().min(0),
  /** `project`: the project audience (capped live by the label); `named`: the readers listed. */
  audience: z.enum(["project", "named"]),
  classification: z.string(),
  /** The classification's level, so the dialog can show its label and download rule. */
  level: classificationLevelSchema.nullable(),
  origin: storedDocumentOriginSchema,
  folderPath: z.array(z.string()),
  existing: artefactInKnowledgeSchema.nullable(),
});
export type ArtefactKnowledgePreview = z.infer<typeof artefactKnowledgePreviewSchema>;

/** The document written: its ID and version, and where it landed. */
export const artefactAddedSchema = z.object({
  documentId: documentIdSchema,
  projectId: projectIdSchema,
  title: z.string(),
  documentVersion: z.int().min(1),
  artefactVersion: z.int().min(1),
  classification: z.string(),
  origin: storedDocumentOriginSchema,
  readerCount: z.int().min(0),
  /** The requested folder, or the Files root (null) when it was deleted meanwhile. */
  folderId: folderIdSchema.nullable(),
});
export type ArtefactAdded = z.infer<typeof artefactAddedSchema>;

/** The download name of a version: a file artefact's title is already its file name. */
export function artefactFileName(kind: ArtefactKind, language: string | null, title: string) {
  return kind === "file" ? title : `${title}.${artefactExtension(kind, language)}`;
}

/** The file extension of a download: the kind's, or the code language's when known. */
export function artefactExtension(kind: ArtefactKind, language: string | null): string {
  switch (kind) {
    case "markdown":
      return "md";
    case "mermaid":
      return "mmd";
    case "svg":
      return "svg";
    case "csv":
      return "csv";
    case "code":
      return (language && CODE_EXTENSIONS[language]) || "txt";
    case "file":
      // A file artefact's title is its whole file name: callers use it as is.
      return "bin";
  }
}

// No `html`: a downloaded code artefact must not open as a page that runs its scripts.
const CODE_EXTENSIONS: Readonly<Record<string, string>> = {
  bash: "sh",
  c: "c",
  "c++": "cpp",
  cpp: "cpp",
  "c#": "cs",
  csharp: "cs",
  css: "css",
  go: "go",
  java: "java",
  javascript: "js",
  js: "js",
  json: "json",
  kotlin: "kt",
  php: "php",
  powershell: "ps1",
  python: "py",
  py: "py",
  r: "r",
  ruby: "rb",
  rust: "rs",
  scala: "scala",
  sh: "sh",
  shell: "sh",
  sql: "sql",
  swift: "swift",
  toml: "toml",
  ts: "ts",
  typescript: "ts",
  xml: "xml",
  yaml: "yaml",
  yml: "yml",
};
